import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { buildSelectOptions } from '../ui/listFilterHelpers';
import {
  collectOrderCategories,
  collectOrderConcepts,
  countActiveOrderFilters,
  DEFAULT_ORDER_LIST_FILTERS,
  getOrderWorkflowFilterOptions,
  normalizeOrderListFilters,
} from './orderListFilterUtils';

const fieldClass = 'rounded-md py-2.5';

/**
 * Multi-criteria order filters in a grid. Combine any fields, then Apply (AND logic).
 */
export function OrderListFiltersPanel({ value, onApply, orders = [], showCommercial = true, className = '' }) {
  const [draft, setDraft] = useState(() => ({ ...DEFAULT_ORDER_LIST_FILTERS }));

  useEffect(() => {
    setDraft(normalizeOrderListFilters(value));
  }, [value]);

  const draftCount = useMemo(() => countActiveOrderFilters(draft), [draft]);

  const orderStatusOptions = useMemo(
    () => getOrderWorkflowFilterOptions().map((o) => ({ value: o.value, label: o.label })),
    []
  );

  const conceptOptions = useMemo(
    () => buildSelectOptions('All concepts', collectOrderConcepts(orders), draft.concept),
    [orders, draft.concept]
  );

  const categoryOptions = useMemo(
    () => buildSelectOptions('All categories', collectOrderCategories(orders), draft.category),
    [orders, draft.category]
  );

  const handleField = (key) => (e) => {
    setDraft((d) => ({ ...d, [key]: e.target.value }));
  };

  const reset = () => {
    const cleared = { ...DEFAULT_ORDER_LIST_FILTERS };
    setDraft(cleared);
    onApply(cleared);
  };

  const apply = () => {
    onApply(normalizeOrderListFilters(draft));
  };

  return (
    <div
      className={[
        'rounded-lg border border-slate-200/90 bg-slate-100/90 p-3 sm:p-4 md:p-5',
        '[&_label]:text-xs [&_label]:text-slate-800',
        className,
      ].join(' ')}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">
          Filter orders
          {draftCount > 0 ? (
            <span className="ml-2 text-xs font-medium text-slate-500">
              {draftCount} active
            </span>
          ) : null}
        </p>
        <button
          type="button"
          onClick={reset}
          className="text-xs font-semibold text-slate-600 transition hover:text-slate-900"
        >
          Reset all
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Input
          type="search"
          label="Search"
          placeholder="Unit, taka no., remark…"
          value={draft.search}
          onChange={handleField('search')}
          className={fieldClass}
        />
        <Input
          type="text"
          label="Order #"
          placeholder="Order number"
          value={draft.orderNo}
          onChange={handleField('orderNo')}
          className={fieldClass}
        />
        <Input
          type="text"
          label="Design name"
          placeholder="Design number"
          value={draft.designNo}
          onChange={handleField('designNo')}
          className={fieldClass}
        />
        <Input
          type="text"
          label="Chalan #"
          placeholder="Chalan number"
          value={draft.chalanNo}
          onChange={handleField('chalanNo')}
          className={fieldClass}
        />
      </div>

      <div
        className={[
          'mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2',
          showCommercial ? 'xl:grid-cols-4' : 'xl:grid-cols-2',
        ].join(' ')}
      >
        {showCommercial ? (
          <>
            <Input
              type="text"
              label="Party"
              placeholder="Customer / party name"
              value={draft.party}
              onChange={handleField('party')}
              className={fieldClass}
            />
            <Input
              type="text"
              label="Broker"
              placeholder="Broker name"
              value={draft.broker}
              onChange={handleField('broker')}
              className={fieldClass}
            />
          </>
        ) : null}
        <Select
          label="Concept"
          value={draft.concept}
          onChange={handleField('concept')}
          options={conceptOptions}
          className={fieldClass}
        />
        <Select
          label="Category"
          value={draft.category}
          onChange={handleField('category')}
          options={categoryOptions}
          className={fieldClass}
        />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-12 xl:items-end">
        <div className="xl:col-span-2">
          <Input
            type="text"
            label="Production unit"
            placeholder="Khata / unit name"
            value={draft.khata}
            onChange={handleField('khata')}
            className={fieldClass}
          />
        </div>
        <div className="xl:col-span-2">
          <Input
            type="text"
            label="Beam color"
            placeholder="Beam color"
            value={draft.beamColor}
            onChange={handleField('beamColor')}
            className={fieldClass}
          />
        </div>
        <div className="xl:col-span-4">
          <Select
            label="Order status"
            value={draft.orderStatus}
            onChange={handleField('orderStatus')}
            options={orderStatusOptions}
            className={fieldClass}
          />
        </div>
        <div className="flex items-end justify-start xl:col-span-4 xl:justify-end">
          <Button
            type="button"
            onClick={apply}
            className="h-11 w-full min-w-[8.5rem] rounded-md px-8 text-base font-semibold shadow-sm xl:w-auto"
          >
            Filter
          </Button>
        </div>
      </div>
    </div>
  );
}

export default OrderListFiltersPanel;
